import { yupResolver } from '@hookform/resolvers/yup'
import { findIndex, isEqual, map } from 'lodash-es'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import { Icon } from '@/common/components/Icon'
import { HtmlEditor } from '@/core/components/HtmlEditor'
import { cn } from '@/lib/utils'
import { CustomBillingField, Languages } from '../../../common/models'
import FormGroup from '../../../core/components/FormGroup'
import {
  deleteCustomBillingField,
  getAllCustomBillingFields,
  getCustomBillingField,
  updateCustomBillingField,
} from '../../../core/services/custom-billing-fields'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import Loader from '@/common/components/Loader'

export interface CreateCustomBillingFieldModalProps {
  showModal: boolean
  field?: CustomBillingField
  setShowModal: (show: boolean) => void
  setFields: (data: CustomBillingField[]) => void
  editMode: boolean
  allLanguages: Languages[]
}

interface IFieldContent {
  locale: string
  name: string
}

interface IFieldValues {
  name: string
  content?: IFieldContent[]
  mandatory: boolean
}

export const CreateCustomBillingFieldModal = (
  props: CreateCustomBillingFieldModalProps,
): JSX.Element => {
  const { showModal, setShowModal, field, editMode, setFields, allLanguages } =
    props

  const [deleteModal, setDeleteModal] = useState(false)
  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')
  const [content, setContent] = useState<IFieldContent[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const temp = map(allLanguages, (lang) => ({
      locale: lang.locale,
      name: '',
    }))
    setValue('content', temp)

    if (field?.id) {
      ;(async () => {
        try {
          setLoading(true)
          const { data: fetchedField } = await getCustomBillingField(field.id!)
          if (
            fetchedField.translations &&
            fetchedField.translations.length > 0
          ) {
            setContent(fetchedField.translations)
            map(fetchedField.translations, (trans) => {
              const index = findIndex(
                allLanguages,
                (l) => l.locale === trans.locale,
              )
              if (index !== -1) {
                setValue(`content.${index}.name`, trans.name)
                setValue(`content.${index}.locale`, trans.locale)
              }
            })
          }
          setValue('name', fetchedField.name, { shouldValidate: true })
          setValue('mandatory', fetchedField.mandatory, {
            shouldValidate: true,
          })
        } catch (e) {
          if (e instanceof Error) {
            setIsError(true)
            setShowMessageBox(true)
            setMessage(e.message)
            setErrorStatus(
              (e as { response?: { status?: number } })?.response?.status,
            )
          } else {
            console.error(e)
          }
        } finally {
          setLoading(false)
        }
      })()
    }

    return () => {
      reset()
    }
  }, [field, showModal])

  const handleClose = () => {
    setShowModal(false)
  }

  const schema = yup.object().shape({
    content: yup
      .array()
      .of(
        yup.object().shape({
          locale: yup.string().nullable().required('Field is required'),
          name: yup.string().required('Field is required'),
        }),
      )
      .required(),
    name: yup.string().required('Field is required'),
    mandatory: yup
      .boolean()
      .oneOf([true, false])
      .required('Field is required')
      .default(false),
  })

  const {
    handleSubmit,
    control,
    register,
    formState: { errors, dirtyFields },
    reset,
    setValue,
    watch,
  } = useForm<IFieldValues>({
    mode: 'onChange',
    resolver: yupResolver<IFieldValues>(schema),
    defaultValues: {
      name: '',
      mandatory: false,
      content: [],
    },
  })

  const contentWatch = watch('content')

  const submit = async (values: IFieldValues) => {
    const data: Omit<CustomBillingField, 'id'> = {
      name: values.name,
      translations: values.content,
      mandatory: values.mandatory,
    }

    if (field?.id) {
      try {
        setLoading(true)
        const { status } = await updateCustomBillingField({
          ...data,
          id: field.id,
        })
        const { data: fields } = await getAllCustomBillingFields()
        setFields(fields)
        if (status === 200) {
          handleClose()
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    } else {
      try {
        setLoading(true)
        const { status } = await updateCustomBillingField(data)
        const { data: fields } = await getAllCustomBillingFields()
        setFields(fields)
        if (status === 200) {
          handleClose()
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    }
  }

  const handleDelete = () => {
    setDeleteModal(true)
  }

  const deleteFieldFromModal = () => {
    // prettier-ignore
    (async () => {
      try {
        if (field) {
          const { status } = await deleteCustomBillingField([field.id!])
          const { data: fields } = await getAllCustomBillingFields()
          setFields(fields)
          if (status === 204) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
    handleClose()
  }

  return (
    <>
      {loading && <Loader />}
      <Modal
        open={showModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              title="Custom Billing Fields"
              subtitle={field ? field.name : 'Add New Custom Billing Fields'}
              icon="text"
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2 border-t border-monochrome-120 pt-4">
            {editMode ? (
              <Button
                onClick={handleDelete}
                variant="destructive"
                className="mr-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div className="min-w-10" />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                form="custom-billing-field-form"
                type="submit"
                variant="primary"
              >
                {field?.id ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="custom-billing-field-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label="Name" error={errors?.name?.message}>
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter Name"
            />
          </FormGroup>

          {map(allLanguages, (language, index) => (
            <div
              className={cn(
                'flex w-full flex-col gap-3 rounded-[30px] border border-monochrome-120 bg-monochrome-120 p-5',
              )}
              key={language.id}
            >
              <FormGroup label="Language" wrapperClassName="mb-0">
                <Input
                  value={language.name}
                  readOnly
                  className="bg-monochrome-120"
                />
              </FormGroup>
              <HtmlEditor
                index={index}
                label="Language"
                name={`content.${index}.name`}
                variables={[]}
                control={control}
                value={contentWatch?.[index]?.name || ''}
                onChange={(data) => {
                  setValue(`content.${index}.name`, data)
                }}
                isError={!!errors?.content?.[index]?.name?.message}
              />
            </div>
          ))}

          <FormGroup
            label="Mandatory Field"
            direction="row"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
            labelProps={{
              htmlFor: 'mandatory',
            }}
          >
            <Controller
              name="mandatory"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="mandatory"
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>
        </form>
      </Modal>
      <DeleteConfirmationModal
        size="md"
        showDeleteConfirmationModal={deleteModal}
        setDeleteConfirmationModal={setDeleteModal}
        deleteTitle="Custom Field"
        deleteFunc={deleteFieldFromModal}
        deletedItem={field?.name}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
